import { clamp, glow, mulberry32, TAU, type DrawFn } from '../lib/math'

export function createPulse(): DrawFn {
  const rng = mulberry32(14)
  const dust = Array.from({ length: 48 }, () => ({
    a: rng() * TAU,
    r: 40 + rng() * 180,
    s: 0.6 + rng() * 1.6,
    o: rng() * TAU,
  }))

  // Heartbeat envelope: lub-dub
  const beat = (t: number) => {
    const p = (t % 1.18) / 1.18
    const lub = Math.exp(-((p - 0.12) ** 2) / 0.0038)
    const dub = Math.exp(-((p - 0.28) ** 2) / 0.0055)
    return lub * 1.0 + dub * 0.7
  }

  return (ctx, t, w, h) => {
    const cx = w * 0.5
    const cy = h * 0.4
    ctx.clearRect(0, 0, w, h)
    ctx.globalCompositeOperation = 'lighter'
    const b = beat(t)
    const intro = clamp(t / 0.8)

    glow(ctx, cx, cy, 200 + b * 50, `rgba(255,60,30,${0.12 + b * 0.12})`)
    glow(ctx, cx, cy, 70 + b * 18, `rgba(255,140,70,${0.28 + b * 0.25})`)
    glow(ctx, cx, cy, 18 + b * 6, `rgba(255,230,200,${0.55 + b * 0.4})`)

    for (let i = 0; i < 6; i++) {
      const phase = (t * 0.55 + i * 0.16) % 1
      const r = 30 + phase * Math.min(w, h) * 0.42
      const alpha = (1 - phase) * 0.42 * intro
      ctx.strokeStyle = `rgba(255,${90 + i * 18},50,${alpha})`
      ctx.lineWidth = 2.4 - phase * 1.6
      ctx.beginPath()
      ctx.arc(cx, cy, r + b * 6, 0, TAU)
      ctx.stroke()
    }

    // ECG trace
    ctx.beginPath()
    const y0 = cy + Math.min(h, 720) * 0.22
    const span = w * 0.78
    const x0 = (w - span) / 2
    const drawn = clamp((t - 0.4) / 1.6)
    ctx.moveTo(x0, y0)
    const steps = 80
    for (let i = 1; i <= steps * drawn; i++) {
      const u = i / steps
      const x = x0 + u * span
      let y = y0
      const local = (u * 2.2 + t * 0.05) % 1
      if (local > 0.42 && local < 0.58) {
        const k = (local - 0.42) / 0.16
        if (k < 0.25) y -= k * 8
        else if (k < 0.4) y += (k - 0.25) * 90
        else if (k < 0.55) y -= (k - 0.4) * 140
        else y += (0.8 - k) * 40
      }
      ctx.lineTo(x, y)
    }
    ctx.strokeStyle = `rgba(255,170,110,${0.55 * intro})`
    ctx.lineWidth = 1.4
    ctx.stroke()

    for (const d of dust) {
      const r = d.r + Math.sin(t * 0.8 + d.o) * 10
      const x = cx + Math.cos(d.a + t * 0.15) * r
      const y = cy + Math.sin(d.a + t * 0.15) * r * 0.72
      ctx.fillStyle = `rgba(255,160,90,${0.25 + b * 0.25})`
      ctx.beginPath()
      ctx.arc(x, y, d.s, 0, TAU)
      ctx.fill()
    }

    ctx.globalCompositeOperation = 'source-over'
  }
}
